import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  Chip,
  IconButton,
  TextField,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { AppPrimaryButton } from 'shared/components/AppComponents';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import { ReactElement, useEffect, useState } from 'react';
import { EnterpriseService } from 'services/EnterpriseService';
import { Utils } from 'shared/helpers';
import TokenCreationModal from './TokenCreationModal';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { IAppState } from 'state/RootReducer';

export interface TokenData {
  _id: string;
  token: string;
  expiryDate: string;
  isActive: boolean;
  createdBy: string;
  organizationId: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

const AISubmission = (): ReactElement => {
  const [isLoading, seIsLoading] = useState<boolean>(false);
  const [tokenList, setTokenList] = useState<TokenData[]>([]);
  const [isTokenModalOpen, setTokenModalOpen] = useState<boolean>(false);
  const [copiedToken, setCopiedToken] = useState<any>(null);
  const [hoveredToken, setHoveredToken] = useState<string | null>(null);
  const { orgACLConfig } = useSelector(
    (state: IAppState) => state.baseAppState
  );

  const getTokenList = (): void => {
    seIsLoading(true);
    EnterpriseService.getTokenList()
      .then((res) => {
        setTokenList(res.data.data);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        seIsLoading(false);
      });
  };

  const handleCopy = (url: any, tokenId: any): void => {
    navigator.clipboard.writeText(url);
    setCopiedToken(tokenId);
    setTimeout(() => setCopiedToken(null), 500);
  };

  const handleDelete = (id: string): void => {
    EnterpriseService.deleteToken(id)
      .then((res) => {
        Utils.handleSuccessResponse(res);
        getTokenList();
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      });
  };

  const onCreateToken = (): void => {
    setTokenModalOpen(true);
  };

  useEffect(() => {
    getTokenList();
  }, []);

  return (
    <Card variant="outlined" className="ai-submission-card">
      <CardHeader
        title="Public AI submission forms"
        action={
          <>
            {orgACLConfig.dashboard.includes(
              'dashboard.enterprisePortal.publicToken'
            ) && (
              <AppPrimaryButton
                title="Create Token"
                startIcon={<AddRoundedIcon />}
                onClick={onCreateToken}
              />
            )}
          </>
        }
      />

      {tokenList.length > 0 && !isLoading && (
        <CardContent sx={{ px: { xs: 1.5, sm: 3 }, pt: 0 }}>
          <Box display="flex" flexDirection="column" gap={2}>
            {tokenList?.map((token) => {
              const link = `${window.location.origin}/public/submit-ai/${token.token}`;

              return (
                <Box
                  key={token._id}
                  sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '12px',
                    p: { xs: 1.5, sm: 2 },
                    position: 'relative',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', lg: 'row' },
                      justifyContent: 'space-between',
                      alignItems: { xs: 'flex-start', lg: 'center' },
                      gap: { xs: 1, lg: 2 },
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: '0.9rem', sm: '1rem' },
                        wordBreak: 'break-word',
                      }}
                    >
                      Submission portal ·{' '}
                      {dayjs(token?.createdAt ?? dayjs()).format('DD/MM/YYYY')}
                      &nbsp;({token?.token.slice(0, 8)}...)
                    </Typography>

                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: { xs: 0.75, sm: 1 },
                        width: { xs: '100%', lg: 'auto' },
                      }}
                    >
                      <Chip
                        label={token.isActive ? 'Active' : 'Inactive'}
                        color={token.isActive ? 'primary' : 'default'}
                        size="small"
                      />

                      <Typography
                        sx={{
                          fontSize: { xs: '12px', sm: '14px' },
                          color: '#6b7280',
                          flex: { xs: '1 1 auto', lg: '0 0 auto' },
                        }}
                      >
                        Expires:{' '}
                        {dayjs(token?.expiryDate ?? dayjs()).format(
                          'DD/MM/YYYY'
                        )}{' '}
                        · 0 submissions
                      </Typography>

                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <Tooltip
                          title={
                            copiedToken === token.token
                              ? 'Copied!'
                              : 'Copy link'
                          }
                          open={
                            copiedToken === token.token ||
                            hoveredToken === token.token
                          }
                          onOpen={() => setHoveredToken(token.token)}
                          onClose={() => setHoveredToken(null)}
                        >
                          <IconButton
                            onClick={() => handleCopy(link, token.token)}
                            size="small"
                            aria-label="Copy link"
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Open in new tab">
                          <IconButton
                            onClick={() => window.open(link, '_blank')}
                            size="small"
                            aria-label="Open in new tab"
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Delete">
                          <IconButton
                            onClick={() => handleDelete(token._id)}
                            size="small"
                            aria-label="Delete token"
                            sx={{
                              border: '1px solid #e0e0e0',
                              borderRadius: '5px',
                              '&:hover': { background: '#f8f9fa' },
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={link}
                      InputProps={{ readOnly: true }}
                      sx={{
                        '& .MuiInputBase-input': {
                          color: '#6b7280',
                          fontSize: { xs: '12px', sm: '14px' },
                          textOverflow: 'ellipsis',
                        },
                      }}
                    />
                  </Box>
                </Box>
              );
            })}
          </Box>
        </CardContent>
      )}

      <TokenCreationModal
        isTokenModalOpen={isTokenModalOpen}
        onClose={() => setTokenModalOpen(false)}
        getTokenList={getTokenList}
      />
      {tokenList.length === 0 && isLoading && (
        <Box className="empty-content">
          <CircularProgress />
        </Box>
      )}
    </Card>
  );
};

export default AISubmission;
